import { jwtDecode } from "jwt-decode";

/**
 * Formata uma data no formato YYYY-MM-DD para DD/MM/AAAA sem sofrer shift de timezone.
 *
 * @param {string|Date} dataStr
 * @returns {string} Data no formato DD/MM/AAAA ou "-" se vazia.
 */
export const formatarDataApenas = (dataStr) => {
  if (!dataStr) return "-";
  const match = /^(\d{4})-(\d{2})-(\d{2})/.exec(String(dataStr));
  if (!match) return dataStr;
  return `${match[3]}/${match[2]}/${match[1]}`;
};

/**
 * Formata data/hora para o padrão brasileiro completo.
 *
 * @param {string|Date} dataStr
 * @returns {string}
 */
export const formatarDataHoraBR = (dataStr) => {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return dataStr;
  return d.toLocaleString("pt-BR");
};

/**
 * Formata data/hora no padrão curto DD/MM/AAAA HH:mm (sem segundos).
 *
 * @param {string|Date} dataStr
 * @returns {string}
 */
export const formatarDataHoraCurta = (dataStr) => {
  if (!dataStr) return "-";
  const d = new Date(dataStr);
  if (isNaN(d.getTime())) return dataStr;
  return d.toLocaleString("pt-BR", {
    year: "numeric",
    month: "2-digit",
    day: "2-digit",
    hour: "2-digit",
    minute: "2-digit",
  });
};

/**
 * Retorna a data/hora em que o cadastro foi concluído e encaminhado para validação.
 *
 * @param {object} item
 * @returns {string|null}
 */
export const dataConclusaoItem = (item) => {
  if (!item) return null;
  return (
    item.data_conclusao ??
    item.dataConclusao ??
    item.data_encaminhamento ??
    item.dataEncaminhamento ??
    item.dt_conclusao_cadastro ??
    item.dtConclusaoCadastro ??
    item.created_at ??
    item.createdAt ??
    null
  );
};

/**
 * Formata CPF ou CNPJ com pontuação padrão.
 *
 * @param {string|number} valor
 * @returns {string}
 */
export const formatarCnpjCpf = (valor) => {
  if (!valor) return "-";
  const nums = String(valor).replace(/\D/g, "").slice(0, 14);
  if (nums.length === 0) return "-";
  if (nums.length <= 11) {
    return nums
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d)/, "$1.$2")
      .replace(/(\d{3})(\d{1,2})$/, "$1-$2");
  }
  return nums
    .replace(/^(\d{2})(\d)/, "$1.$2")
    .replace(/^(\d{2})\.(\d{3})(\d)/, "$1.$2.$3")
    .replace(/\.(\d{3})(\d)/, ".$1/$2")
    .replace(/(\d{4})(\d{1,2})$/, "$1-$2");
};

/**
 * Extrai de forma resiliente a lista de documentos do payload da API.
 *
 * @param {any} payload
 * @returns {Array<object>}
 */
export const extrairListaValidacoes = (payload) => {
  if (!payload) return [];
  if (Array.isArray(payload)) return payload;
  if (Array.isArray(payload.data)) return payload.data;
  if (Array.isArray(payload.rows)) return payload.rows;
  if (Array.isArray(payload.validacoes)) return payload.validacoes;
  if (Array.isArray(payload.itens)) return payload.itens;
  if (Array.isArray(payload.items)) return payload.items;
  return [];
};

/**
 * Resolve o ID canônico do documento/contrato.
 *
 * @param {object} item
 * @returns {number|string|null}
 */
export const resolverIdDoc = (item) => {
  if (!item) return null;
  return (
    item.id ??
    item.tce_id ??
    item.tca_empregador_id ??
    item.tca_id ??
    item.autorizacao_id ??
    null
  );
};

/**
 * Resolve o CNPJ/documento da empresa a partir de qualquer formato de payload (TCE, TCA, TCA Empregador).
 *
 * @param {object} item
 * @returns {string}
 */
export const resolverCnpjDoc = (item) => {
  if (!item) return "";
  return String(
    item.empresa?.cnpj ||
      item.cnpj ||
      item.empresa_cnpj ||
      item.cnpj_empresa ||
      item.cnpj_ou_cpf ||
      item.cnpj_cpf ||
      item.empresa?.documento ||
      item.documento_empresa ||
      "",
  ).trim();
};

/**
 * Resolve a Razão Social / nome da empresa de forma abrangente.
 *
 * @param {object} item
 * @returns {string}
 */
export const resolverNomeEmpresaDoc = (item) => {
  if (!item) return "-";
  const nome =
    item.empresa?.razao_social ||
    item.empresa?.razaosocial ||
    item.empresa?.nome_empresa ||
    item.empresa?.nome ||
    item.razao_social ||
    item.razaosocial ||
    item.nome_empresa ||
    item.empresa_nome ||
    item.concedente_nome ||
    "";
  return String(nome).trim() || "-";
};

/**
 * Resolve o nome do estudante / aprendiz de forma abrangente.
 *
 * @param {object} item
 * @returns {string}
 */
export const resolverNomeEstudanteDoc = (item) => {
  if (!item) return "-";
  const nome =
    item.nome_aprendiz ||
    item.nome_estagiario ||
    item.estudante_nome ||
    item.nome_estudante ||
    item.nome_completo ||
    item.estudante?.nome ||
    item.aprendiz?.nome ||
    "";
  return String(nome).trim() || "-";
};

/**
 * Resolve o CPF do estudante / aprendiz.
 *
 * @param {object} item
 * @returns {string}
 */
export const resolverCpfEstudanteDoc = (item) => {
  if (!item) return "";
  return String(
    item.cpf ||
      item.cpf_aprendiz ||
      item.cpf_estagiario ||
      item.estudante_cpf ||
      item.aprendiz_cpf ||
      item.estudante?.cpf ||
      item.aprendiz?.cpf ||
      "",
  ).replace(/\D/g, "");
};

/**
 * Classifica o documento pelo _origem anotado na resposta da API.
 *
 * @param {object} item
 * @returns {string}
 */
export const tipoDoc = (item) => {
  if (!item) return "—";
  const origem = item?._origem;
  if (origem === "TCA-Empregador" || origem === "tca-empregador") return "TCA Empregador";
  if (origem === "TCA" || origem === "tca") return "TCA";
  if (origem === "TCE" || origem === "tce") return "TCE";
  return "—";
};

/**
 * Rota para redirecionamento do formulário correspondente.
 *
 * @param {object} item
 * @returns {string}
 */
export const rotaDoc = (item) => {
  if (!item) return "/tca";
  const id = resolverIdDoc(item);
  const origem = item?._origem;
  if (origem === "TCA-Empregador" || origem === "tca-empregador") return `/tca-empregador?id=${id}`;
  if (origem === "TCE" || origem === "tce") return `/tce?id=${id}`;
  if (origem === "TCA" || origem === "tca") return `/tca?id=${id}`;
  return `/tca?id=${id}`;
};

/**
 * Endpoint para atribuição do documento para o operador.
 *
 * @param {object} item
 * @returns {string}
 */
export const endpointAtribuir = (item) => {
  if (!item) return "/tca/validacoes/atribuir/";
  const id = resolverIdDoc(item);
  const origem = item?._origem;
  if (origem === "TCA-Empregador" || origem === "tca-empregador") return `/tca-empregador/validacoes/atribuir/${id}`;
  if (origem === "TCE" || origem === "tce") return `/tce/validacoes/atribuir/${id}`;
  return `/tca/validacoes/atribuir/${id}`;
};

/**
 * Endpoint para liberação do documento de volta para a fila.
 *
 * @param {object} item
 * @returns {string}
 */
export const endpointLiberar = (item) => {
  if (!item) return "/tca/validacoes/liberar/";
  const id = resolverIdDoc(item);
  const origem = item?._origem;
  if (origem === "TCA-Empregador" || origem === "tca-empregador") return `/tca-empregador/validacoes/liberar/${id}`;
  if (origem === "TCE" || origem === "tce") return `/tce/validacoes/liberar/${id}`;
  return `/tca/validacoes/liberar/${id}`;
};

/**
 * Remove documentos duplicados entre chamadas paralelas.
 *
 * @param {Array} lista
 * @returns {Array}
 */
export const deduplicar = (lista) => {
  const seen = new Set();
  const out = [];
  for (const item of lista) {
    if (!item) continue;
    const id = resolverIdDoc(item);
    const key = `${tipoDoc(item)}-${id}`;
    if (seen.has(key)) continue;
    seen.add(key);
    out.push(item.id !== undefined ? item : { ...item, id });
  }
  return out;
};

/**
 * Ordena a lista de forma que os mais recentes fiquem no topo.
 *
 * @param {Array} lista
 * @returns {Array}
 */
export const ordenarPorMaisRecente = (lista) => {
  return [...lista].sort((a, b) => {
    const tA = new Date(dataConclusaoItem(a) || a.created_at || 0).getTime();
    const tB = new Date(dataConclusaoItem(b) || b.created_at || 0).getTime();
    if (tB !== tA) return tB - tA;
    return (Number(b.id) || 0) - (Number(a.id) || 0);
  });
};

/**
 * Recorta a página da lista de itens.
 *
 * @param {Array} lista
 * @param {number} pagina
 * @param {number} tamanho
 * @returns {{ itens: Array, totalPaginas: number, total: number, paginaSegura: number }}
 */
export const recortarPagina = (lista, pagina, tamanho) => {
  const total = lista.length;
  const totalPaginas = Math.max(1, Math.ceil(total / tamanho));
  const paginaSegura = Math.min(Math.max(1, pagina), totalPaginas);
  const inicio = (paginaSegura - 1) * tamanho;
  const itens = lista.slice(inicio, inicio + tamanho);
  return { itens, totalPaginas, total, paginaSegura };
};

/**
 * Ajusta a página atual dentro dos limites válidos.
 *
 * @param {Array} lista
 * @param {number} pagina
 * @param {number} tamanho
 * @returns {number}
 */
export const ajustarPagina = (lista, pagina, tamanho) => {
  const { paginaSegura } = recortarPagina(lista, pagina, tamanho);
  return paginaSegura;
};

/**
 * Filtra documentos por termo de busca e tipo de contrato.
 *
 * @param {Array} lista
 * @param {string} termo
 * @param {string} tipo
 * @returns {Array}
 */
export const filtrarLista = (lista, termo, tipo) => {
  let filtrada = lista;
  if (tipo && tipo !== "todos") {
    filtrada = filtrada.filter((item) => item._origem === tipo);
  }
  if (termo && termo.trim()) {
    const termoRaw = termo.trim();
    const temHash = termoRaw.startsWith("#");
    const q = termoRaw.toLowerCase().replace(/^#/, "");
    const qNums = q.replace(/\D/g, "");
    const isNumeroPuro = /^\d+$/.test(q);

    filtrada = filtrada.filter((item) => {
      const idStr = String(resolverIdDoc(item) || "");

      if (temHash || (isNumeroPuro && q.length <= 4)) {
        return idStr === q;
      }

      const docRef = `${item.estudante_id || ""}-${item.empresa_id || ""}`.toLowerCase();
      const nomeEst = resolverNomeEstudanteDoc(item).toLowerCase();
      const nomeEmp = resolverNomeEmpresaDoc(item).toLowerCase();
      const cnpjEmp = resolverCnpjDoc(item).replace(/\D/g, "");
      const cpfEst = resolverCpfEstudanteDoc(item);

      const matchCpfCnpj =
        qNums.length >= 3 &&
        (cnpjEmp.includes(qNums) || cpfEst.includes(qNums));

      return (
        idStr === q ||
        docRef.includes(q) ||
        nomeEst.includes(q) ||
        nomeEmp.includes(q) ||
        matchCpfCnpj
      );
    });
  }
  return filtrada;
};

/**
 * Conta a distribuição de documentos por tipo de programa.
 *
 * @param {Array} lista
 * @returns {{ todos: number, tcaEmpregador: number, tca: number, tce: number }}
 */
export const contarPorTipo = (lista = []) => {
  let todos = 0;
  let tcaEmpregador = 0;
  let tca = 0;
  let tce = 0;
  for (const it of lista) {
    if (!it) continue;
    todos++;
    if (it._origem === "TCA-Empregador" || it._origem === "tca-empregador") {
      tcaEmpregador++;
    } else if (it._origem === "TCA" || it._origem === "tca") {
      tca++;
    } else if (it._origem === "TCE" || it._origem === "tce") {
      tce++;
    }
  }
  return { todos, tcaEmpregador, tca, tce };
};

/**
 * Hidrata instantaneamente a fila "Novos" a partir do cache compartilhado com a Sidebar.
 *
 * @param {string} apiBaseUrl
 * @returns {Array<object>}
 */
export const obterCacheFilaNovosInicial = (apiBaseUrl) => {
  if (typeof window === "undefined") return [];
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") return [];

  if (
    window.__cieeValidacoesNovosListaCache &&
    Date.now() - window.__cieeValidacoesNovosListaCache.ts < 60000 &&
    Array.isArray(window.__cieeValidacoesNovosListaCache.lista)
  ) {
    return window.__cieeValidacoesNovosListaCache.lista;
  }

  const cacheUrls = window.__cieeValidacoesNovosCache;
  if (!cacheUrls || !apiBaseUrl) return [];

  const mapa = [
    { url: `${apiBaseUrl}/tca/validacoes/novos`, label: "TCA" },
    { url: `${apiBaseUrl}/tce/validacoes/novos`, label: "TCE" },
    { url: `${apiBaseUrl}/tca-empregador/validacoes/novos`, label: "TCA-Empregador" },
  ];

  const acumulados = [];
  for (const m of mapa) {
    const entry = cacheUrls[m.url];
    if (entry?.data && Date.now() - entry.ts < 60000) {
      const lista = extrairListaValidacoes(entry.data);
      for (const it of lista) {
        acumulados.push({ ...it, _origem: m.label });
      }
    }
  }

  return acumulados.length > 0 ? ordenarPorMaisRecente(deduplicar(acumulados)) : [];
};

/**
 * Hidrata instantaneamente a fila "Minhas Validações" a partir do cache em memória.
 *
 * @returns {Array<object>}
 */
export const obterCacheFilaMinhasInicial = () => {
  if (typeof window === "undefined") return [];
  if (typeof process !== "undefined" && process.env?.NODE_ENV === "test") return [];

  if (
    window.__cieeValidacoesMinhasListaCache &&
    Date.now() - window.__cieeValidacoesMinhasListaCache.ts < 60000 &&
    Array.isArray(window.__cieeValidacoesMinhasListaCache.lista)
  ) {
    return window.__cieeValidacoesMinhasListaCache.lista;
  }
  return [];
};

/**
 * Verifica se o usuário tem permissão para acessar a fila de Validações (nível CIEE >= 2).
 *
 * @param {string} [token]
 * @param {object} [userObj]
 * @returns {boolean}
 */
export const verificarPermissaoValidacoes = (token, userObj) => {
  const t = token ?? (typeof window !== "undefined" ? localStorage.getItem("token") : null);
  if (!t) return false;

  try {
    const decoded = jwtDecode(t);
    if (decoded.tipo === "estudante" || decoded.tipo === "empresa") return false;
    if (decoded.tipo !== "usuario_ciee") return false;

    let nivel = Number(
      decoded.acesso_autorizacao ??
        decoded.tipo_acesso ??
        decoded.nivel_acesso ??
        decoded.role_id ??
        0,
    );

    if (!nivel) {
      try {
        const u =
          userObj ??
          (typeof window !== "undefined"
            ? JSON.parse(localStorage.getItem("user") || "{}")
            : {});
        nivel = Number(
          u?.acesso_autorizacao ??
            u?.tipo_acesso ??
            u?.nivel_acesso ??
            u?.role_id ??
            0,
        );
      } catch (_) {}
    }

    return nivel >= 2;
  } catch {
    return false;
  }
};
